'use client';
import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import Image from 'next/image';
import { NAV, LANGUAGE_OPTIONS, NOTICE } from '@/shared/utils/const';
import GlobeIcon from '@/public/images/pc/header/globe.svg';
import ProfitIcon from '@/public/images/pc/header/profit.svg';
import DataRefreshIcon from '@/public/images/pc/header/data-refresh.svg';
import LogoutIcon from '@/public/images/pc/header/logout.svg';
import { signOutWithCleanup } from '@/entities/auth/api/clientAuth';
import { GetDcaRefresh } from '@/entities/dca/api/getDcaRefresh';
import { useNoticeData } from '@/features/notice/hook/useNoticeData';
import { handleLanguageChange } from '@/shared/lib/clientLocale';
import { NoticeList } from '@/features/notice/ui/NoticeList';
import { useCurrentLocale } from '@/shared/i18n/I18nProvider';
import { useTranslations } from 'use-intl';
import { useSession } from '@/app/providers';

interface NavLinkItem {
  href: string;
  label: string;
  target?: '_blank' | '_self';
}

export const HeaderPC = ({ profile }: { profile: getProfileRes | null }) => {
  const router = useRouter();
  const pathname = usePathname();

  // Provider에서 가져옴
  const t = useTranslations();
  const locale = useCurrentLocale();

  const session = useSession();
  const [globeIsHovered, setGlobeIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isNoticeOpen, setIsNoticeOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const noticeRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const langDropdownRef = useRef<HTMLDivElement>(null);

  const { unreadCnt, handleReadAll } = useNoticeData(null);

  const iconFill = globeIsHovered || isOpen ? '#FAEA4D' : '#8F9194';

  const navLinks: NavLinkItem[] = [
    { href: '/v2/trade', label: t('GNB.1') },
    { href: 'https://super-trader.gitbook.io/super-trader', label: t('GNB.2'), target: '_blank' },
    { href: 'https://open.kakao.com/o/sNmW9NHf', label: t('GNB.3'), target: '_blank' },
  ];

  const maskingEmail = (email: string) => {
    const [id, domain] = email.split('@');
    return `${id.length > 3 ? id.slice(0, 3) : id}***@${domain}`;
  };

  const displayLoginID = (login: string) => {
    // 이메일이면 *** 처리
    if (login.includes('@')) {
      return maskingEmail(login);
    }
    // 전화번호(이메일 아님)면 전체 노출
    return login;
  };

  const handleLogout = () => {
    signOutWithCleanup();
    setUserDropdownOpen(false);
  };

  const handleDataRefresh = async () => {
    await GetDcaRefresh();
    window.location.reload();
  };

  const handleLanguageSelect = (value: string) => {
    handleLanguageChange(value, () => {
      setIsOpen(false);
      setGlobeIsHovered(false); // 글로브 아이콘
    });
  };

  // URL 변경되면 알림/User 닫기
  useEffect(() => {
    setIsNoticeOpen(false);
    setUserDropdownOpen(false);
  }, [pathname]);

  // 바깥 클릭 시 닫힘
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (userDropdownRef.current && !userDropdownRef.current.contains(target)) {
        setUserDropdownOpen(false);
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
      if (noticeRef.current && !noticeRef.current.contains(target)) {
        setIsNoticeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="pc-header">
      <Link href="/v2" className="pc-logo">
        <Image src={NAV.pc.logo} width={158} height={23} alt="logo" unoptimized />
      </Link>

      <nav className="flex items-center h-full">
        {navLinks.map(({ href, label, target }) => (
          <Link
            key={href}
            href={href}
            target={target}
            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
            className={`inline-flex items-center h-full px-[16px] hover:text-font-basic ${pathname.includes(href) ? 'text-font-basic' : ''
              }`}
          >
            {label}
          </Link>
        ))}
      </nav>

      <div className="inline-flex items-center gap-[24px] ml-auto">
        {
          profile?.id_login ? (
            <>
              {/* 유저 드롭다운 */}
              <div className="relative" ref={userDropdownRef}>
                <button
                  className={`pc-masking-email 
              ${userDropdownOpen ? 'bg-primary-yellow text-font-black' : 'bg-[#32363D] text-font-white'}
            `}
                  onClick={() => setUserDropdownOpen(prev => !prev)}
                >
                  {displayLoginID(profile?.id_login ?? '')}
                </button>

                {userDropdownOpen && (
                  <div className="pc-user-dropdown">
                    <div className="px-[16px] mb-[16px]">
                      <p className="mb-[6px] text-font-white text-[14px] font-normal tracking-[-0.35px]">
                        {displayLoginID(profile?.id_login ?? '')}
                      </p>
                      <p className="pc-uuid">UID : {profile?.uuid}</p>
                    </div>
                    <Link href="/v2/profit" className="pc-btn-header">
                      <ProfitIcon className="fill-current" width={14} height={14} fill={iconFill} />
                      {t('GNB.Profit')}
                    </Link>
                    <button className="pc-btn-header" onClick={handleDataRefresh}>
                      <DataRefreshIcon className="fill-current" width={14} height={14} />
                      {t('Profile.refresh')}
                    </button>
                    <button className="pc-btn-header" onClick={handleLogout}>
                      <LogoutIcon className="fill-current" width={14} height={14} />
                      {t('GNB.Logout')}
                    </button>
                  </div>
                )}
              </div>

              {/* 알림 */}
              <div className="relative">
                <button onClick={() => setIsNoticeOpen((prev) => !prev)} className="pc-alarm">
                  <Image src={NAV.pc.notice} width={24} height={24} alt="notice" unoptimized />
                  {
                    Number(unreadCnt) > 0 && (
                      <span className="alarm-count">
                        {Number(unreadCnt) > 99 ? '+99' : unreadCnt}
                      </span>
                    )
                  }
                </button>

                {isNoticeOpen && (
                  <div className="pc-header-notice-list mt-[8px]" ref={noticeRef}>
                    <div className="h-[16px] mr-[24px]">
                      {Number(unreadCnt) > 0 && (
                        <button
                          onClick={handleReadAll}
                          className="flex items-center gap-[8px] mb-[8px] ml-[auto] text-[16px] font-semibold text-font-basic leading-none"
                        >
                          {t('Notice.ReadAll')}
                          <Image src={NOTICE.mo.read} width={14} height={14} alt="all read" />
                        </button>
                      )}
                    </div>

                    <NoticeList />
                    <div className="pc-header-notice-footer h-[25px] pt-[6px]">
                      <button
                        onClick={() => {
                          setIsNoticeOpen(false);
                          router.push('/v2/notice');
                        }}
                        className="flex items-center justify-end ml-auto gap-[4px] text-[16px] font-semibold text-font-basic tracking-[-0.4px] leading-none"
                      >
                        {t('Notice.FullView')}
                        <Image src={NOTICE.mo.allView} width={16} height={16} alt="all view" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <a href="/login" className="pc-login-btn">
              {t('GNB.Login')}
            </a>
            // <Link href="/v2/signIn" className="pc-login-btn">
            //   로그인
            // </Link>
          )}

        {/* 다국어 선택 */}
        <div
          className="relative flex-shrink-0"
          ref={langDropdownRef}
          onMouseEnter={() => setGlobeIsHovered(true)}
          onMouseLeave={() => setGlobeIsHovered(false)}
        >
          <button onClick={() => setIsOpen((prev) => !prev)} className="flex items-center">
            <GlobeIcon width={24} height={24} fill={globeIsHovered || isOpen ? '#FAEA4D' : '#8F9194'} />
          </button>

          {isOpen && (
            <div className="pc-lang-dropdown">
              {LANGUAGE_OPTIONS.map((option) => {
                const isActive = option.value === locale;
                const isKR = option.value === 'KR';
                return (
                  // 24.09.04 KR빼고 비활성화 처리
                  <button
                    key={option.value}
                    className={`pc-lang-btn-bordered ${isActive ? 'font-color-primary-yellow' : ''
                      } ${!isKR ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => isKR && handleLanguageSelect(option.value)}
                    disabled={!isKR}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};